import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import { EmissionPointPicker } from '../../../components/bookkeeping/EmissionPointPicker.js';
import { SriStatusTrack } from '../../../components/bookkeeping/SriStatusTrack.js';
import type {
  Account,
  AccountNode,
  Customer,
  Invoice,
  Item,
  TaxRate,
} from '../../../types/bookkeeping.js';
import type { ConfigStatus } from '../../../types/onboarding.js';

const FX_SCALE = 1_000_000_000n;
const QTY_SCALE = 1_000n;
const TAX_DIVISOR = 100_000_000n;

const CM_REASONS = [
  'return_full',
  'return_partial',
  'discount',
  'price_adjustment',
  'quantity_adjustment',
  'cancellation',
  'allowance',
  'other',
] as const;
type CMReason = (typeof CM_REASONS)[number];

function flattenTree(nodes: AccountNode[]): Account[] {
  const result: Account[] = [];
  function walk(ns: AccountNode[]) {
    for (const n of ns) {
      result.push(n);
      if (n.children.length > 0) walk(n.children);
    }
  }
  walk(nodes);
  return result;
}

interface CMLineForm {
  item_id: string;
  warehouse_id: string;
  description: string;
  quantity: string;
  unit_price: string;
  tax_rate_id: string;
  account_id: string;
  source_invoice_line_id: string;
}

function defaultLine(): CMLineForm {
  return {
    item_id: '',
    warehouse_id: '',
    description: '',
    quantity: '1000',
    unit_price: '0',
    tax_rate_id: '',
    account_id: '',
    source_invoice_line_id: '',
  };
}

interface CMDetail {
  credit_memo: {
    id: string;
    number: string;
    customer_id: string;
    source_invoice_id: string | null;
    entry_date: string;
    reason: CMReason;
    currency: string;
    fx_rate: string;
    subtotal: string;
    tax_total: string;
    grand_total: string;
    applied_amount: string;
    status: string;
    memo: string | null;
    clave_acceso: string | null;
    signed_xml: string | null;
    signed_at: string | null;
  };
  lines: Array<{
    id: string;
    line_number: number;
    item_id: string | null;
    warehouse_id: string | null;
    description: string | null;
    quantity: string;
    unit_price: string;
    line_subtotal: string;
    tax_rate_id: string;
    tax_amount: string;
    line_total: string;
    account_id: string;
    source_invoice_line_id: string | null;
  }>;
  applications: Array<{
    id: string;
    invoice_id: string;
    applied_amount: string;
    applied_date: string;
  }>;
}

export function CreditMemoForm() {
  const { t } = useTranslation('bookkeeping');
  const nav = useNavigate();
  const qc = useQueryClient();
  const { activeOrgId } = useAuth();
  const { id: cmId } = useParams<{ id: string }>();
  const isNew = !cmId;

  const today = new Date().toISOString().slice(0, 10);
  const [customerId, setCustomerId] = useState('');
  const [sourceInvoiceId, setSourceInvoiceId] = useState('');
  const [entryDate, setEntryDate] = useState(today);
  const [reason, setReason] = useState<CMReason>('return_partial');
  const [currency, setCurrency] = useState('');
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<CMLineForm[]>([defaultLine()]);
  const [seeded, setSeeded] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [emissionPointId, setEmissionPointId] = useState<string | null>(null);

  // Apply UI state (only shown for existing open CM with applied < grand)
  const [applyInvoiceId, setApplyInvoiceId] = useState('');
  const [applyAmount, setApplyAmount] = useState('');
  const [applyError, setApplyError] = useState<string | null>(null);

  const { data: configData } = useQuery({
    queryKey: ['config-status', activeOrgId],
    queryFn: () => api<ConfigStatus>(`/orgs/${activeOrgId}/config-status`),
    enabled: !!activeOrgId,
  });
  const baseCurrency = configData?.organization.baseCurrency ?? 'USD';
  const effectiveCurrency = currency || baseCurrency;
  const sameCurrency = effectiveCurrency === baseCurrency || effectiveCurrency.length !== 3;

  const { data: fxData } = useQuery({
    queryKey: ['fx', effectiveCurrency, baseCurrency, entryDate],
    queryFn: () =>
      api<{ rate: string; source: string }>(
        `/bookkeeping/fx-rates?date=${entryDate}&from=${effectiveCurrency}&to=${baseCurrency}`,
      ),
    enabled: !!effectiveCurrency && effectiveCurrency.length === 3 && !sameCurrency,
    retry: false,
  });

  const { data: customersData } = useQuery({
    queryKey: ['bookkeeping-customers', activeOrgId],
    queryFn: () => api<{ customers: Customer[] }>('/bookkeeping/customers?limit=200'),
    enabled: !!activeOrgId,
  });
  const customers = (customersData?.customers ?? []).filter((c) => c.is_active);

  // Fetch invoices of selected customer (for source_invoice_id picker + apply target)
  const { data: invoicesData } = useQuery({
    queryKey: ['bk-customer-invoices', activeOrgId, customerId],
    queryFn: () =>
      api<{ invoices: Invoice[] }>(
        `/bookkeeping/sales/invoices?customer_id=${customerId}&limit=100`,
      ),
    enabled: !!activeOrgId && !!customerId,
  });
  const invoices = invoicesData?.invoices ?? [];
  const openOrPartialInvoices = invoices.filter(
    (i) => i.status === 'open' || i.status === 'partial',
  );

  const { data: itemsData } = useQuery({
    queryKey: ['bookkeeping-items', activeOrgId],
    queryFn: () => api<{ items: Item[] }>('/bookkeeping/items?limit=200'),
    enabled: !!activeOrgId,
  });
  const items = (itemsData?.items ?? []).filter((i) => i.is_active);

  const { data: taxRatesData } = useQuery({
    queryKey: ['bookkeeping-tax-rates', activeOrgId],
    queryFn: () => api<{ tax_rates: TaxRate[] }>('/bookkeeping/sales/tax-rates?limit=100'),
    enabled: !!activeOrgId,
  });
  const taxRates = (taxRatesData?.tax_rates ?? []).filter((r) => r.is_active);

  const { data: accountsData } = useQuery({
    queryKey: ['bookkeeping-accounts', activeOrgId],
    queryFn: () => api<{ tree: AccountNode[] }>('/bookkeeping/accounts'),
    enabled: !!activeOrgId,
  });
  const allAccounts = flattenTree(accountsData?.tree ?? []);
  const revenueAccounts = allAccounts.filter((a) => a.type === 'revenue' && a.is_active);

  // Existing CM detail
  const cmDetailQ = useQuery({
    queryKey: ['bk-credit-memo', cmId],
    queryFn: () => api<CMDetail>(`/bookkeeping/sales/credit-memos/${cmId}`),
    enabled: !isNew && !!cmId,
    onSuccess: (d: CMDetail) => {
      if (seeded) return;
      setCustomerId(d.credit_memo.customer_id);
      setSourceInvoiceId(d.credit_memo.source_invoice_id ?? '');
      setEntryDate(d.credit_memo.entry_date);
      setReason(d.credit_memo.reason);
      setCurrency(d.credit_memo.currency);
      setMemo(d.credit_memo.memo ?? '');
      setLines(
        d.lines.map(
          (l): CMLineForm => ({
            item_id: l.item_id ?? '',
            warehouse_id: l.warehouse_id ?? '',
            description: l.description ?? '',
            quantity: l.quantity,
            unit_price: l.unit_price,
            tax_rate_id: l.tax_rate_id,
            account_id: l.account_id,
            source_invoice_line_id: l.source_invoice_line_id ?? '',
          }),
        ),
      );
      setSeeded(true);
    },
  } as Parameters<typeof useQuery>[0]);

  const cmDetail = cmDetailQ.data as CMDetail | undefined;
  const cmStatus = cmDetail?.credit_memo.status;
  const cmGrand = cmDetail ? BigInt(cmDetail.credit_memo.grand_total) : 0n;
  const cmApplied = cmDetail ? BigInt(cmDetail.credit_memo.applied_amount) : 0n;
  const cmRemaining = cmGrand - cmApplied;

  // Live totals
  let subtotalBig = 0n;
  let taxBig = 0n;
  for (const l of lines) {
    try {
      if (l.quantity && l.unit_price) {
        const qty = BigInt(l.quantity);
        const price = BigInt(l.unit_price);
        const lineSub = (qty * price) / QTY_SCALE;
        subtotalBig += lineSub;
        const tr = taxRates.find((r) => r.id === l.tax_rate_id);
        if (tr) taxBig += (lineSub * BigInt(tr.percentage)) / TAX_DIVISOR;
      }
    } catch {
      /* ignore */
    }
  }
  const grandBig = subtotalBig + taxBig;

  const canSubmit =
    isNew &&
    !!customerId &&
    !!entryDate &&
    effectiveCurrency.length === 3 &&
    lines.length >= 1 &&
    lines.every((l) => l.account_id && l.tax_rate_id && l.quantity && l.unit_price);

  const submit = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({
        customer_id: customerId,
        source_invoice_id: sourceInvoiceId || null,
        entry_date: entryDate,
        reason,
        currency: effectiveCurrency,
        fx_rate: sameCurrency ? FX_SCALE.toString() : (fxData?.rate ?? FX_SCALE.toString()),
        memo: memo || null,
        emission_point_id: emissionPointId ?? undefined,
        lines: lines.map((l) => ({
          item_id: l.item_id || null,
          warehouse_id: l.warehouse_id || null,
          description: l.description || null,
          quantity: l.quantity,
          unit_price: l.unit_price,
          tax_rate_id: l.tax_rate_id,
          account_id: l.account_id,
          source_invoice_line_id: l.source_invoice_line_id || null,
        })),
      });
      return api('/bookkeeping/sales/credit-memos', { method: 'POST', body });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['bk-credit-memos'] });
      nav('/app/bookkeeping/sales/credit-memos');
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setSubmitError(err.body?.error ?? err.message ?? 'Error');
    },
  });

  const apply = useMutation({
    mutationFn: () =>
      api(`/bookkeeping/sales/credit-memos/${cmId}/apply`, {
        method: 'POST',
        body: JSON.stringify({
          applications: [{ invoice_id: applyInvoiceId, applied_amount: applyAmount }],
          applied_date: new Date().toISOString().slice(0, 10),
        }),
      }),
    onSuccess: async () => {
      setApplyInvoiceId('');
      setApplyAmount('');
      setApplyError(null);
      await qc.invalidateQueries({ queryKey: ['bk-credit-memo', cmId] });
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setApplyError(err.body?.error ?? err.message ?? 'Error');
    },
  });

  const voidMut = useMutation({
    mutationFn: () => api(`/bookkeeping/sales/credit-memos/${cmId}/void`, { method: 'POST' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['bk-credit-memos'] });
      nav('/app/bookkeeping/sales/credit-memos');
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          {isNew ? t('sales.creditMemos.new') : t('sales.creditMemos.editTitle')}
        </h1>
        <button
          type="button"
          onClick={() => nav('/app/bookkeeping/sales/credit-memos')}
          className="rounded border px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          {t('journalEntries.cancel')}
        </button>
      </div>

      {/* Emission point picker */}
      <EmissionPointPicker value={emissionPointId} onChange={setEmissionPointId} />

      {/* Header */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <div>
          <label className="block text-xs text-slate-500" htmlFor="customer">
            {t('sales.creditMemos.fields.customer')} *
          </label>
          <select
            id="customer"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            disabled={!isNew}
            className="mt-0.5 w-full rounded border px-2 py-1 text-sm disabled:bg-slate-50"
          >
            <option value="">— {t('sales.creditMemos.fields.customer')}</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-slate-500" htmlFor="src-inv">
            {t('sales.creditMemos.fields.source_invoice')}
          </label>
          <select
            id="src-inv"
            value={sourceInvoiceId}
            onChange={(e) => setSourceInvoiceId(e.target.value)}
            disabled={!isNew || !customerId}
            className="mt-0.5 w-full rounded border px-2 py-1 text-sm disabled:bg-slate-50"
          >
            <option value="">— {t('sales.creditMemos.fields.source_invoice_optional')}</option>
            {invoices.map((i) => (
              <option key={i.id} value={i.id}>
                {i.number} ({i.status})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-slate-500" htmlFor="reason">
            {t('sales.creditMemos.fields.reason')} *
          </label>
          <select
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value as CMReason)}
            disabled={!isNew}
            className="mt-0.5 w-full rounded border px-2 py-1 text-sm disabled:bg-slate-50"
          >
            {CM_REASONS.map((r) => (
              <option key={r} value={r}>
                {t(`sales.creditMemos.reasons.${r}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-slate-500" htmlFor="entry-date">
            {t('sales.creditMemos.fields.entry_date')} *
          </label>
          <input
            id="entry-date"
            type="date"
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
            disabled={!isNew}
            className="mt-0.5 block w-full rounded border px-2 py-1 text-sm disabled:bg-slate-50"
          />
        </div>

        <div>
          <label className="block text-xs text-slate-500" htmlFor="currency">
            {t('sales.creditMemos.fields.currency')}
          </label>
          <input
            id="currency"
            value={effectiveCurrency}
            maxLength={3}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            disabled={!isNew}
            className="mt-0.5 block w-full rounded border px-2 py-1 text-sm uppercase disabled:bg-slate-50"
          />
        </div>

        <div className="col-span-2 md:col-span-3">
          <label className="block text-xs text-slate-500" htmlFor="memo">
            {t('sales.creditMemos.fields.memo')}
          </label>
          <input
            id="memo"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            disabled={!isNew}
            className="mt-0.5 block w-full rounded border px-2 py-1 text-sm disabled:bg-slate-50"
          />
        </div>
      </div>

      {/* Lines */}
      <div className="rounded border bg-white">
        <div className="border-b bg-slate-50 px-3 py-2 text-sm font-semibold">
          {t('sales.creditMemos.lines.title')}
        </div>
        <table className="w-full text-sm">
          <thead className="border-b text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-2 py-1.5">{t('sales.creditMemos.lines.item')}</th>
              <th className="px-2 py-1.5">{t('sales.creditMemos.lines.account')}</th>
              <th className="px-2 py-1.5">{t('sales.creditMemos.lines.description')}</th>
              <th className="px-2 py-1.5 text-right">{t('sales.creditMemos.lines.qty')}</th>
              <th className="px-2 py-1.5 text-right">{t('sales.creditMemos.lines.unit_price')}</th>
              <th className="px-2 py-1.5">{t('sales.creditMemos.lines.tax_rate')}</th>
              <th className="px-2 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, idx) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: credit memo lines are ordered form rows with no stable ID
              <tr key={idx} className="border-b last:border-0">
                <td className="px-2 py-1">
                  <select
                    value={l.item_id}
                    onChange={(e) => {
                      const next = [...lines];
                      next[idx] = { ...l, item_id: e.target.value };
                      setLines(next);
                    }}
                    disabled={!isNew}
                    className="w-full rounded border px-1.5 py-0.5 text-xs disabled:bg-slate-50"
                  >
                    <option value="">—</option>
                    {items.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1">
                  <select
                    value={l.account_id}
                    onChange={(e) => {
                      const next = [...lines];
                      next[idx] = { ...l, account_id: e.target.value };
                      setLines(next);
                    }}
                    disabled={!isNew}
                    className="w-full rounded border px-1.5 py-0.5 text-xs disabled:bg-slate-50"
                  >
                    <option value="">—</option>
                    {revenueAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} {a.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1">
                  <input
                    value={l.description}
                    onChange={(e) => {
                      const next = [...lines];
                      next[idx] = { ...l, description: e.target.value };
                      setLines(next);
                    }}
                    disabled={!isNew}
                    className="w-full rounded border px-1.5 py-0.5 text-xs disabled:bg-slate-50"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    value={l.quantity}
                    onChange={(e) => {
                      const next = [...lines];
                      next[idx] = { ...l, quantity: e.target.value };
                      setLines(next);
                    }}
                    disabled={!isNew}
                    className="w-full rounded border px-1.5 py-0.5 text-right font-mono text-xs disabled:bg-slate-50"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    value={l.unit_price}
                    onChange={(e) => {
                      const next = [...lines];
                      next[idx] = { ...l, unit_price: e.target.value };
                      setLines(next);
                    }}
                    disabled={!isNew}
                    className="w-full rounded border px-1.5 py-0.5 text-right font-mono text-xs disabled:bg-slate-50"
                  />
                </td>
                <td className="px-2 py-1">
                  <select
                    value={l.tax_rate_id}
                    onChange={(e) => {
                      const next = [...lines];
                      next[idx] = { ...l, tax_rate_id: e.target.value };
                      setLines(next);
                    }}
                    disabled={!isNew}
                    className="w-full rounded border px-1.5 py-0.5 text-xs disabled:bg-slate-50"
                  >
                    <option value="">—</option>
                    {taxRates.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.code}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1 text-right">
                  {isNew && (
                    <button
                      type="button"
                      onClick={() => setLines(lines.filter((_, i) => i !== idx))}
                      className="text-xs text-red-600 hover:underline"
                    >
                      ✕
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {isNew && (
          <div className="border-t bg-slate-50 px-3 py-2">
            <button
              type="button"
              onClick={() => setLines([...lines, defaultLine()])}
              className="text-xs text-blue-600 hover:underline"
            >
              + {t('sales.creditMemos.lines.add')}
            </button>
          </div>
        )}
      </div>

      {/* Apply UI — only for existing open CM with remaining */}
      {!isNew && cmStatus === 'open' && cmRemaining > 0n && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3">
          <div className="mb-2 text-sm font-semibold">{t('sales.creditMemos.apply.title')}</div>
          <div className="mb-2 text-xs text-slate-600">
            {t('sales.creditMemos.apply.remaining')}:{' '}
            <span className="font-mono">{cmRemaining.toString()}</span>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-slate-500" htmlFor="apply-invoice">
                {t('sales.creditMemos.apply.invoice')}
              </label>
              <select
                id="apply-invoice"
                value={applyInvoiceId}
                onChange={(e) => setApplyInvoiceId(e.target.value)}
                className="mt-0.5 w-full rounded border px-2 py-1 text-sm"
              >
                <option value="">— {t('sales.creditMemos.apply.select_invoice')}</option>
                {openOrPartialInvoices.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.number} — {i.grand_total} ({i.status})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500" htmlFor="apply-amount">
                {t('sales.creditMemos.apply.amount')}
              </label>
              <input
                id="apply-amount"
                value={applyAmount}
                onChange={(e) => setApplyAmount(e.target.value)}
                className="mt-0.5 w-32 rounded border px-2 py-1 text-right font-mono text-sm"
              />
            </div>
            <button
              type="button"
              disabled={!applyInvoiceId || !applyAmount || apply.isPending}
              onClick={() => apply.mutate()}
              className="rounded bg-amber-600 px-3 py-1.5 text-sm text-white hover:bg-amber-700 disabled:bg-slate-300"
            >
              {t('sales.creditMemos.apply.submit')}
            </button>
          </div>
          {applyError && <p className="mt-2 text-xs text-red-700">{applyError}</p>}
        </div>
      )}

      {/* Existing applications */}
      {!isNew && cmDetail && cmDetail.applications.length > 0 && (
        <div className="rounded border bg-white">
          <div className="border-b bg-slate-50 px-3 py-2 text-sm font-semibold">
            {t('sales.creditMemos.applications.title')}
          </div>
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-1.5">{t('sales.creditMemos.applications.invoice')}</th>
                <th className="px-3 py-1.5">{t('sales.creditMemos.applications.date')}</th>
                <th className="px-3 py-1.5 text-right">
                  {t('sales.creditMemos.applications.amount')}
                </th>
              </tr>
            </thead>
            <tbody>
              {cmDetail.applications.map((a) => (
                <tr key={a.id} className="border-b last:border-0">
                  <td className="px-3 py-1.5 font-mono text-xs">{a.invoice_id.slice(0, 8)}</td>
                  <td className="px-3 py-1.5">{a.applied_date}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{a.applied_amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Totals + actions */}
      <div className="flex items-end justify-between rounded border bg-white p-3">
        <div className="space-y-0.5 text-sm">
          <div>
            {t('sales.creditMemos.totals.subtotal')}:{' '}
            <span className="font-mono">
              {(isNew ? subtotalBig : BigInt(cmDetail?.credit_memo.subtotal ?? '0')).toString()}
            </span>
          </div>
          <div>
            {t('sales.creditMemos.totals.tax')}:{' '}
            <span className="font-mono">
              {(isNew ? taxBig : BigInt(cmDetail?.credit_memo.tax_total ?? '0')).toString()}
            </span>
          </div>
          <div className="border-t pt-1 text-base">
            {t('sales.creditMemos.totals.grand')}:{' '}
            <span className="font-mono font-bold">{(isNew ? grandBig : cmGrand).toString()}</span>
          </div>
          {!isNew && (
            <div className="text-xs text-slate-500">
              {t('sales.creditMemos.totals.applied')}:{' '}
              <span className="font-mono">{cmApplied.toString()}</span> ·{' '}
              {t('sales.creditMemos.totals.remaining')}:{' '}
              <span className="font-mono">{cmRemaining.toString()}</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {!isNew && cmStatus !== 'void' && (
            <button
              type="button"
              onClick={() => voidMut.mutate()}
              className="rounded border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
            >
              {t('sales.creditMemos.actions.void')}
            </button>
          )}
          {isNew && (
            <button
              type="button"
              disabled={!canSubmit || submit.isPending}
              onClick={() => submit.mutate()}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:bg-slate-300"
            >
              {t('sales.creditMemos.actions.submit')}
            </button>
          )}
        </div>
      </div>

      {submitError && (
        <p className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {submitError}
        </p>
      )}

      {/* SRI delivery block — only for saved credit memos with clave_acceso */}
      {!isNew && cmDetail?.credit_memo.clave_acceso && (
        <SriStatusTrack docType="credit-memos" docId={cmDetail.credit_memo.id} />
      )}
    </div>
  );
}
